import React, { useState, useEffect, useCallback } from 'react';
import ttsTestService from '../utils/ttsTestService';
import googleTTSService from '../utils/googleTTSService';
import appScriptService from '../utils/appScriptService';

const TTSTest = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentChunk, setCurrentChunk] = useState(-1);
  const [voices, setVoices] = useState([]);
  const [selectedVoiceIndex, setSelectedVoiceIndex] = useState(0);
  
  // 모듈 선택 상태
  const [modules] = useState([
    { code: 'LM2001020230', name: '프로그래밍언어응용' },
    { code: 'LM2001020231', name: '프로그래밍언어활용' },
    { code: 'LM2001020224', name: '화면설계' },
    { code: 'LM2001020225', name: '화면구현' },
    { code: 'LM2001020211', name: '서버프로그램구현' },
    { code: 'LM2001020227', name: '애플리케이션테스트수행' },
    { code: 'LM2001020205', name: '데이터입출력구현'       },
    { code: 'LM2001020206', name: '통합구현'               },
    { code: 'LM2001020214', name: '애플리케이션배포'       },
    { code: 'LM2001020212', name: '인터페이스구현'         },
    // 추가 카테고리,hasData: false, isChecking: true 
    { code: 'BE001_SPRING', name: 'Spring 백엔드'          },
    { code: 'FE001_REACT', name: 'React 프론트엔드'        },
    { code: 'HR001_COMMON', name: '공통 면접질문'          },
  ]);
  const [selectedModule, setSelectedModule] = useState('LM2001020230');
  const [moduleData, setModuleData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Google Cloud TTS 상태
  const [ttsMethod, setTtsMethod] = useState('browser');
  const [isGenerating, setIsGenerating] = useState(false);
  const [podcastScript, setPodcastScript] = useState('');
  const [sections, setSections] = useState([]);
  const [downloadProgress, setDownloadProgress] = useState({});
  const [isDownloading, setIsDownloading] = useState(false);

  // 모듈 데이터 로드
  const loadModuleData = useCallback(async (moduleCode) => {

    setIsLoading(true);
    
    try {
      const data = await appScriptService.getTTSScripts(moduleCode);

      
      setModuleData(data);
      
      // 섹션 데이터 저장
      if (data.sections && data.sections.length > 0) {
        setSections(data.sections);
        const fullScript = data.sections.map(section => section.content).join('\n\n');
        setPodcastScript(fullScript);
      } else {
        setSections([]);
        setPodcastScript('섹션 데이터를 찾을 수 없습니다.');
      }
    } catch (error) {
      console.error('❌ 모듈 데이터 로드 실패:', error);
      // 기본 스크립트 설정
      setModuleData(null);
      setSections([]);
      setPodcastScript('테스트용 기본 스크립트입니다. 모듈 데이터를 불러올 수 없습니다.');
    } finally {
      setIsLoading(false);
    }
  }, []); // 의존성 배열에서 isLoading 제거

  // 초기화 useEffect
  useEffect(() => {
    // 음성 로드 완료 후 한국어 음성 목록 가져오기
    const loadVoices = () => {
      const availableVoices = ttsTestService.getKoreanVoices();
      if (availableVoices.length > 0) {
        setVoices(availableVoices);

      } else {
        // 음성이 없으면 재시도
        setTimeout(loadVoices, 500);
      }
    };
    
    setTimeout(loadVoices, 1000);
  }, []);
  
  // 모듈 변경시에만 데이터 로드
  useEffect(() => {

    loadModuleData(selectedModule);
  }, [selectedModule, loadModuleData]);

  const handlePlay = async () => {
    if (isPlaying) {
      ttsTestService.stop();
      setIsPlaying(false);
      setCurrentChunk(-1);
      return;
    }

    setIsPlaying(true);
    
    try {
      await ttsTestService.speakPodcastScript(
        podcastScript,
        // 청크 시작 콜백
        (index, chunk) => {

          setCurrentChunk(index);
        },
        // 청크 완료 콜백
        (index, chunk) => {

        }
      );
      
      setIsPlaying(false);
      setCurrentChunk(-1);
      
    } catch (error) {
      console.error('TTS 재생 실패:', error);
      setIsPlaying(false);
      setCurrentChunk(-1);
    }
  };

  const handleVoiceChange = (index) => {
    setSelectedVoiceIndex(index);
    ttsTestService.setVoice(index);
  };
  
  const handleModuleChange = (moduleCode) => {

    
    // 동일한 모듈이거나 로딩 중이면 무시
    if (moduleCode === selectedModule) {

      return;
    }
    
    if (isLoading) {

      return;
    }
    

    // 이전 상태들 먼저 초기화
    setDownloadProgress({});
    setIsPlaying(false);
    setCurrentChunk(-1);
    
    // 모듈 변경 (이것이 useEffect를 트리거함)
    setSelectedModule(moduleCode);
  };
  
  // 개별 섹션 다운로드
  const handleSectionDownload = async (sectionIndex, section) => {
    const sectionKey = `section_${sectionIndex}`;
    
    try {
      setDownloadProgress(prev => ({ ...prev, [sectionKey]: { status: 'generating', progress: 0 } }));
      
      // 섹션 내용이 너무 길면 청크로 분할
      const chunks = googleTTSService.splitTextIntoChunks(section.content, 4800);
      const audioUrls = [];
      
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        const progress = Math.round(((chunkIndex + 1) / chunks.length) * 100);
        setDownloadProgress(prev => ({
          ...prev, 
          [sectionKey]: { status: 'generating', progress, chunk: chunkIndex + 1, totalChunks: chunks.length }
        }));
        
        try {
          // 프록시 API 사용
          const response = await fetch('/api/tts', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              text: chunks[chunkIndex],
              options: {
                languageCode: 'ko-KR',
                voiceName: 'ko-KR-Chirp3-HD-Algieba',
                gender: 'FEMALE',
                speakingRate: 1.0,
                pitch: 0.0
              }
            })
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || `HTTP ${response.status}`);
          }
          
          const data = await response.json();
          const audioUrl = googleTTSService.createAudioUrl(data.audioContent);
          audioUrls.push(audioUrl);
          
          // API 제한 방지 딜레이
          if (chunkIndex < chunks.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } catch (error) {
          console.error(`섹션 ${sectionIndex + 1}, 청크 ${chunkIndex + 1} 생성 실패:`, error);
          // 다운로드 진행률 업데이트 시 상태 리셋 방지
          setDownloadProgress(prev => ({ 
            ...prev, 
            [sectionKey]: { status: 'error', error: error.message } 
          }));
          return;
        }
      }
      
      // 다운로드 시작
      setDownloadProgress(prev => ({ ...prev, [sectionKey]: { status: 'downloading' } }));
      
      if (audioUrls.length === 1) {
        // 단일 파일
        googleTTSService.downloadAudio(audioUrls[0], `${section.title}.mp3`);
      } else {
        // 여러 파일
        await googleTTSService.downloadMultipleAudios(audioUrls, section.title);
      }
      
      // 완료 상태로 업데이트 (상태 리셋 방지)
      setDownloadProgress(prev => ({ 
        ...prev, 
        [sectionKey]: { status: 'completed' } 
      }));
      
      // 5초 후 상태 초기화 (더 긴 시간으로 변경)
      setTimeout(() => {
        setDownloadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[sectionKey];
          return newProgress;
        });
      }, 5000);
      
    } catch (error) {
      console.error(`섹션 ${sectionIndex + 1} 다운로드 실패:`, error);
      // 오류 상태 업데이트 시에도 상태 리셋 방지
      setDownloadProgress(prev => ({ 
        ...prev, 
        [sectionKey]: { status: 'error', error: error.message } 
      }));
    }
  };
  
  // 전체 섹션 다운로드
  const handleAllSectionsDownload = async () => {
    if (!sections.length) return;
    
    setIsDownloading(true);
    
    for (let i = 0; i < sections.length; i++) {
      await handleSectionDownload(i, sections[i]);
      // 섹션 간 딜레이
      if (i < sections.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
    
    setIsDownloading(false);
  };

  const scriptChunks = podcastScript.split('\n\n').filter(chunk => chunk.trim().length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* 헤더 */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
            🎙️ NCS 모듈 TTS 테스트
          </h1>
          <p className="text-gray-600 text-lg">
            선택한 NCS 모듈의 스크립트를 음성으로 들어보세요
          </p>
          {moduleData && (
            <p className="text-blue-600 font-medium mt-2">
              현재 모듈: {moduleData.title}
            </p>
          )}
        </div>

        {/* 컨트롤 패널 */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* 모듈 선택 */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                NCS 모듈 선택
              </label>
              <select
                value={selectedModule}
                onChange={(e) => handleModuleChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                disabled={isPlaying || isLoading}
              >
                {modules.map((module) => (
                  <option key={module.code} value={module.code}>
                    {module.name} ({module.code})
                  </option>
                ))}
              </select>
            </div>

            {/* 재생 컨트롤 */}
            <div className="flex gap-2">
              <button
                onClick={handlePlay}
                disabled={voices.length === 0 || isLoading || !podcastScript}
                className={`px-6 py-3 rounded-lg font-semibold text-white transition-all duration-300 ${
                  isPlaying
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoading ? '로딩...' : isPlaying ? '⏹️ 중지' : '▶️ 재생'}
              </button>
              
              {isPlaying && (
                <button
                  onClick={() => ttsTestService.pause()}
                  className="px-4 py-3 bg-yellow-600 text-white rounded-lg font-semibold hover:bg-yellow-700"
                >
                  ⏸️ 일시정지
                </button>
              )}
            </div>
          </div>

          {/* 현재 재생 상태 */}
          {isPlaying && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <div className="text-sm text-blue-700 font-medium">
                현재 재생 중: 섹션 {currentChunk + 1}/{scriptChunks.length}
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2 mt-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${((currentChunk + 1) / scriptChunks.length) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
        
        {/* 섹션별 다운로드 컨트롤 */}
        {sections.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">🎵 섹션별 음성 다운로드</h2>
              <button
                onClick={handleAllSectionsDownload}
                disabled={isDownloading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50"
              >
                {isDownloading ? '전체 다운로드 중...' : '🔄 전체 다운로드'}
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sections.map((section, index) => {
                const sectionKey = `section_${index}`;
                const progress = downloadProgress[sectionKey];
                const textLength = section.content.length;
                const estimatedChunks = Math.ceil(textLength / 4800);
                
                return (
                  <div key={index} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-800 mb-1">
                          {section.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {textLength}자 • 예상 청크: {estimatedChunks}개
                        </p>
                      </div>
                      
                      <button
                        onClick={() => handleSectionDownload(index, section)}
                        disabled={progress?.status === 'generating' || progress?.status === 'downloading'}
                        className="ml-3 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        {progress?.status === 'generating' ? '생성 중...' :
                         progress?.status === 'downloading' ? '다운로드 중...' :
                         progress?.status === 'completed' ? '✅ 완료' :
                         progress?.status === 'error' ? '❌ 실패' :
                         '⬇️ 다운로드'}
                      </button>
                    </div>
                    
                    {/* 진행률 표시 */}
                    {progress && progress.status === 'generating' && (
                      <div className="mt-2">
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                          <span>청크 {progress.chunk}/{progress.totalChunks}</span>
                          <span>{progress.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div 
                            className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${progress.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                    
                    {/* 오류 메시지 */}
                    {progress?.status === 'error' && (
                      <div className="mt-2 text-xs text-red-600">
                        오류: {progress.error}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 스크립트 표시 */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-semibold mb-4">📜 모듈 스크립트 (재생용)</h2>
          {isLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <p className="mt-2 text-gray-600">모듈 데이터 로딩 중...</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {scriptChunks.map((chunk, index) => (
                <div 
                  key={index} 
                  className={`p-3 rounded-lg transition-all duration-300 ${
                    currentChunk === index 
                      ? 'bg-blue-100 border-l-4 border-blue-500' 
                      : 'bg-gray-50'
                  }`}
                >
                  <div className="text-sm text-gray-500 mb-1">섹션 {index + 1}</div>
                  <div className="text-gray-700 leading-relaxed">
                    {chunk}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 음성 품질 피드백 */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mt-6">
          <h2 className="text-xl font-semibold mb-4">🎯 테스트 포인트</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h3 className="font-medium text-gray-800 mb-2">확인사항:</h3>
              <ul className="space-y-1 text-gray-600">
                <li>• 발음의 정확성</li>
                <li>• 자연스러운 억양</li>
                <li>• 적절한 속도</li>
                <li>• 단어 간 쉼</li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium text-gray-800 mb-2">평가기준:</h3>
              <ul className="space-y-1 text-gray-600">
                <li>• 전문용어 발음</li>
                <li>• 문장 연결의 자연스러움</li>
                <li>• 듣기 편안함</li>
                <li>• 집중도 유지</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TTSTest;